/**
 * Dashboard Service
 * Handles all dashboard-related API calls
 */

import { apiService, ApiResponse } from './api.service';

/**
 * Dashboard Statistics Interface
 * This matches what our backend returns
 */
export interface DashboardStats {
  totalMenus: number;
  totalRevenue: number;
  totalOrders: number;
  totalCustomers: number;
  revenueChange: number; // Percentage change
  ordersChange: number;
  customersChange: number;
}

/**
 * Order Interface for Dashboard
 */
export interface DashboardOrder {
  id: string;
  orderNumber: string;
  customerName: string;
  total: number;
  status: 'PENDING' | 'CONFIRMED' | 'PREPARING' | 'READY' | 'DELIVERED' | 'CANCELLED';
  orderDate: string;
  items: number; // Number of items in order
}

/**
 * Revenue Data Point for Charts
 */
export interface RevenueDataPoint {
  date: string;
  revenue: number;
  orders: number;
}

/**
 * Dashboard Analytics
 */
export interface DashboardAnalytics {
  date: string;
  totalOrders: number;
  totalRevenue: number;
  totalCustomers: number;
  avgOrderValue: number;
  newCustomers: number;
  returningCustomers: number;
}

/**
 * Dashboard Service
 * Handles all dashboard-related API calls
 */
class DashboardService {
  /**
   * Get dashboard statistics
   * Returns totals for menus, revenue, orders, and customers
   */
  async getStats(tenantId: string, dateRange?: { start: Date; end: Date }): Promise<ApiResponse<DashboardStats>> {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (dateRange) {
        params.append('startDate', dateRange.start.toISOString());
        params.append('endDate', dateRange.end.toISOString());
      }

      const queryString = params.toString() ? `?${params.toString()}` : '';
      return await apiService.get<DashboardStats>(`/analytics/dashboard/${tenantId}${queryString}`);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      throw error;
    }
  }

  /**
   * Get recent orders for dashboard
   * Returns the latest orders (typically last 5-10)
   */
  async getRecentOrders(tenantId: string, limit = 5): Promise<ApiResponse<DashboardOrder[]>> {
    try {
      return await apiService.get<DashboardOrder[]>(`/analytics/recent-orders/${tenantId}?limit=${limit}`);
    } catch (error) {
      console.error('Failed to fetch recent orders:', error);
      throw error;
    }
  }

  /**
   * Get revenue data for charts
   * Returns daily revenue and order counts
   */
  async getRevenueData(tenantId: string, dateRange: { start: Date; end: Date }): Promise<ApiResponse<RevenueDataPoint[]>> {
    try {
      const params = new URLSearchParams({
        startDate: dateRange.start.toISOString(),
        endDate: dateRange.end.toISOString(),
      });

      return await apiService.get<RevenueDataPoint[]>(`/analytics/revenue/${tenantId}?${params.toString()}`);
    } catch (error) {
      console.error('Failed to fetch revenue data:', error);
      throw error;
    }
  }

  /**
   * Get analytics data for date range
   * Returns detailed analytics by date
   */
  async getAnalytics(dateRange: { start: Date; end: Date }): Promise<ApiResponse<DashboardAnalytics[]>> {
    try {
      const params = new URLSearchParams({
        startDate: dateRange.start.toISOString(),
        endDate: dateRange.end.toISOString(),
      });

      return await apiService.get<DashboardAnalytics[]>(`/analytics?${params.toString()}`);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      throw error;
    }
  }

  /**
   * Get product count (for Total Menus card)
   */
  async getProductCount(): Promise<ApiResponse<{ count: number }>> {
    try {
      return await apiService.get<{ count: number }>('/products/count');
    } catch (error) {
      console.error('Failed to fetch product count:', error);
      throw error;
    }
  }

  /**
   * Get customer count
   */
  async getCustomerCount(): Promise<ApiResponse<{ count: number }>> {
    try {
      return await apiService.get<{ count: number }>('/customers/count');
    } catch (error) {
      console.error('Failed to fetch customer count:', error);
      throw error;
    }
  }

  /**
   * Get orders per table (for table-based ordering)
   */
  async getOrdersPerTable(tenantId: string, dateRange?: { start: Date; end: Date }): Promise<ApiResponse<Array<{ table: string; orders: number; capacity: number }>>> {
    try {
      const params = new URLSearchParams();
      if (dateRange) {
        params.append('startDate', dateRange.start.toISOString());
        params.append('endDate', dateRange.end.toISOString());
      }

      const queryString = params.toString() ? `?${params.toString()}` : '';
      return await apiService.get<Array<{ table: string; orders: number; capacity: number }>>(`/analytics/orders-per-table/${tenantId}${queryString}`);
    } catch (error) {
      console.error('Failed to fetch orders per table:', error);
      throw error;
    }
  }

  /**
   * Get customer orders distribution
   */
  async getCustomerOrdersDistribution(tenantId: string, dateRange?: { start: Date; end: Date }): Promise<ApiResponse<Array<{ customer: string; orders: number }>>> {
    try {
      const params = new URLSearchParams();
      if (dateRange) {
        params.append('startDate', dateRange.start.toISOString());
        params.append('endDate', dateRange.end.toISOString());
      }

      const queryString = params.toString() ? `?${params.toString()}` : '';
      return await apiService.get<Array<{ customer: string; orders: number }>>(`/analytics/customer-orders-distribution/${tenantId}${queryString}`);
    } catch (error) {
      console.error('Failed to fetch customer orders distribution:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const dashboardService = new DashboardService();